import { useState, useRef, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Image, X, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const UNITS = [
  { value: "zirben", label: "Zirben Chalet" },
  { value: "landhaus", label: "Landhaus" },
  { value: "general", label: "Allgemein" },
];

const CATEGORIES = [
  { value: "gallery", label: "Galerie" },
  { value: "exterior", label: "Aussen" },
  { value: "rooms", label: "Zimmer" },
  { value: "hero", label: "Hero" },
  { value: "nature", label: "Umgebung" },
];

export default function ImageManager({ files, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [unitId, setUnitId] = useState("zirben");
  const [category, setCategory] = useState("gallery");
  const [filterUnit, setFilterUnit] = useState("all");
  const [previewImg, setPreviewImg] = useState(null);
  const fileInputRef = useRef(null);

  const uploadFiles = useCallback(async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    let ok = 0;
    let fail = 0;
    for (const file of fileList) {
      if (!file.type.startsWith("image/")) { fail++; continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: Zu gross (max 10 MB)`); fail++; continue; }
      try {
        const form = new FormData();
        form.append("file", file);
        await api.post(`/admin/upload?unit_id=${unitId}&category=${category}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (e) {
        toast.error(`${file.name}: Upload fehlgeschlagen`);
        fail++;
      }
    }
    if (ok > 0) toast.success(`${ok} Bild${ok > 1 ? "er" : ""} hochgeladen`);
    if (fail > 0) toast.error(`${fail} fehlgeschlagen`);
    setUploading(false);
    onRefresh?.();
  }, [unitId, category, onRefresh]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  }, [uploadFiles]);

  const handleDelete = async (fileId) => {
    if (!window.confirm("Bild wirklich loeschen?")) return;
    try {
      await api.delete(`/admin/files/${fileId}`);
      toast.success("Geloescht");
      onRefresh?.();
    } catch { toast.error("Loeschen fehlgeschlagen"); }
  };

  const filtered = filterUnit === "all" ? files : files.filter(f => f.unit_id === filterUnit);

  return (
    <div data-testid="image-manager">
      {/* Upload area */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--foreground))]/10 p-6 mb-8">
        <div className="flex items-baseline justify-between mb-5">
          <div>
            <div className="overline">Bilder hochladen</div>
            <h2 className="font-serif text-2xl text-[hsl(var(--foreground))] mt-1">Neue Fotos hinzufuegen</h2>
          </div>
        </div>

        {/* Unit + Category selectors */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <div>
            <label className="overline block mb-2">Unterkunft</label>
            <select
              data-testid="upload-unit"
              value={unitId}
              onChange={e => setUnitId(e.target.value)}
              className="w-full bg-[hsl(var(--input))] border border-[hsl(var(--foreground))]/15 text-[hsl(var(--foreground))] py-2.5 px-3 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
            >
              {UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-2">Kategorie</label>
            <select
              data-testid="upload-category"
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="w-full bg-[hsl(var(--input))] border border-[hsl(var(--foreground))]/15 text-[hsl(var(--foreground))] py-2.5 px-3 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
            >
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
        </div>

        {/* Drop zone */}
        <div
          data-testid="upload-dropzone"
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
            dragOver
              ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5"
              : "border-[hsl(var(--foreground))]/20 hover:border-[hsl(var(--accent))]/50 hover:bg-[hsl(var(--muted))]/50"
          }`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 size={32} className="animate-spin text-[hsl(var(--accent))]" />
              <p className="text-sm text-[hsl(var(--foreground))]/70">Wird hochgeladen...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload size={32} className="text-[hsl(var(--foreground))]/40" />
              <p className="text-sm font-medium text-[hsl(var(--foreground))]/80">
                Bilder hierher ziehen oder klicken
              </p>
              <p className="text-xs text-[hsl(var(--foreground))]/50">JPG, PNG, WebP — max. 10 MB pro Bild</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={e => uploadFiles(e.target.files)}
          />
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          onClick={() => setFilterUnit("all")}
          className={`px-4 py-2 text-xs tracking-wider uppercase font-medium border transition-colors ${
            filterUnit === "all"
              ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
              : "bg-[hsl(var(--card))] text-[hsl(var(--foreground))]/70 border-[hsl(var(--foreground))]/15 hover:border-[hsl(var(--accent))]"
          }`}
        >
          Alle ({files.length})
        </button>
        {UNITS.map(u => {
          const count = files.filter(f => f.unit_id === u.value).length;
          return (
            <button
              key={u.value}
              onClick={() => setFilterUnit(u.value)}
              className={`px-4 py-2 text-xs tracking-wider uppercase font-medium border transition-colors ${
                filterUnit === u.value
                  ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                  : "bg-[hsl(var(--card))] text-[hsl(var(--foreground))]/70 border-[hsl(var(--foreground))]/15 hover:border-[hsl(var(--accent))]"
              }`}
            >
              {u.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Image grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-[hsl(var(--foreground))]/50">
          <Image size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Noch keine Bilder hochgeladen</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map(f => (
            <div
              key={f.id}
              data-testid={`image-card-${f.id}`}
              className="group relative bg-[hsl(var(--card))] border border-[hsl(var(--foreground))]/10 overflow-hidden"
            >
              <div
                className="aspect-[4/3] cursor-pointer"
                onClick={() => setPreviewImg(f)}
              >
                <img
                  src={`${API}/api/files/${f.storage_path}`}
                  alt={f.original_filename}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-[10px] tracking-wider uppercase font-medium px-2 py-0.5 bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]/70">
                      {UNITS.find(u => u.value === f.unit_id)?.label || f.unit_id}
                    </span>
                    <span className="text-[10px] tracking-wider uppercase font-medium px-2 py-0.5 bg-[hsl(var(--moss))] text-[hsl(var(--foreground))]/70 ml-1">
                      {CATEGORIES.find(c => c.value === f.category)?.label || f.category}
                    </span>
                  </div>
                  <button
                    data-testid={`delete-image-${f.id}`}
                    onClick={() => handleDelete(f.id)}
                    className="p-1.5 text-red-500/60 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Loeschen"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <p className="text-[11px] text-[hsl(var(--foreground))]/50 mt-1.5 truncate">{f.original_filename}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox preview */}
      {previewImg && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewImg(null)}
        >
          <button
            className="absolute top-6 right-6 text-white/80 hover:text-white"
            onClick={() => setPreviewImg(null)}
          >
            <X size={28} />
          </button>
          <img
            src={`${API}/api/files/${previewImg.storage_path}`}
            alt={previewImg.original_filename}
            className="max-w-full max-h-[85vh] object-contain"
          />
        </div>
      )}
    </div>
  );
}
